// src/components/StatsCounter.tsx
import React, { useState, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

interface StatsCounterProps {
  endValue: number;
  duration?: number;
  label: string;
  suffix?: string;
}

const StatsCounter: React.FC<StatsCounterProps> = ({
  endValue,
  duration = 2000,
  label,
  suffix = "",
}) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (inView) {
      startTimeRef.current = null;
      setCount(0);

      const animateCount = (timestamp: DOMHighResTimeStamp) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }

        const progress = (timestamp - startTimeRef.current) / duration;
        const currentCount = Math.min(
          Math.floor(progress * endValue),
          endValue
        );
        setCount(currentCount);

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animateCount);
        } else {
          setCount(endValue);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateCount);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setCount(0);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [inView, endValue, duration]);

  return (
    // This div encapsulates the "single-counter" and "counter-contents" concepts
    <div ref={ref} className="text-center">
      {/* Equivalent to <h2> with <span class="counter-number"> and <span>+</span> */}
      <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">
        <span className="counter-number">{count}</span>{" "}
        {/* Removed direct suffix here to match structure more closely */}
        <span>{suffix}</span>
      </h2>
      {/* Equivalent to <h3 class="counter-heading"> */}
      <h3 className="text-lg text-gray-200 uppercase tracking-wide">{label}</h3>
    </div>
  );
};

export default StatsCounter;
